import { useTranslation } from "react-i18next";
import { sectionHeadingStyle, labelStyle } from "./eventSectionStyles.js";
import { formatScheduleDay } from "../../utils/eventFormat.js";

const rowStyle = {
  display: "grid",
  gridTemplateColumns: "88px minmax(0, 1fr)",
  gap: "20px",
  padding: "18px 0",
  borderTop: "1px solid rgba(254,250,239,0.1)",
};

function ScheduleRow({ item, isLast }) {
  return (
    <li style={{ ...rowStyle, borderBottom: isLast ? "1px solid rgba(254,250,239,0.1)" : "none" }}>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: "var(--color-gold)" }}>{item.time}</span>
      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <span style={{ fontSize: "16px", color: "var(--color-cream)" }}>{item.title}</span>
        {item.description && (
          <span style={{ fontSize: "14px", lineHeight: 1.65, color: "var(--color-cream-soft)" }}>{item.description}</span>
        )}
      </div>
    </li>
  );
}

/**
 * The running order. `<ol>` because the sequence is the content — read out
 * of order it says nothing.
 *
 * Two shapes are accepted. A single-day event keeps the flat list it
 * always had; a multi-day one groups by day:
 *
 *   schedule:
 *     - date: "2026-08-05"
 *       label: "Anreise"
 *       items: [{ time, title, description }]
 *
 * A group is recognised by its `items` — nothing else has to be declared,
 * and a five-day con doesn't force a day wrapper onto a tavern evening.
 */
export default function EventSchedule({ items, lang }) {
  const { t } = useTranslation();
  if (!items?.length) return null;

  const isGrouped = items.some((entry) => Array.isArray(entry.items));

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      <h2 style={sectionHeadingStyle}>{t("events.scheduleTitle")}</h2>

      {isGrouped ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
          {items.map((day, index) => {
            const dayLabel = [formatScheduleDay(day.date, lang), day.label].filter(Boolean).join(" · ");
            return (
              <div key={day.date || day.label || index} style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {dayLabel && <h3 style={{ ...labelStyle, fontSize: "11px", margin: 0 }}>{dayLabel}</h3>}
                <ol style={{ display: "flex", flexDirection: "column", margin: 0, padding: 0, listStyle: "none" }}>
                  {(day.items ?? []).map((item, i) => (
                    <ScheduleRow key={`${item.time}-${item.title}`} item={item} isLast={i === day.items.length - 1} />
                  ))}
                </ol>
              </div>
            );
          })}
        </div>
      ) : (
        <ol style={{ display: "flex", flexDirection: "column", margin: 0, padding: 0, listStyle: "none" }}>
          {items.map((item, index) => (
            <ScheduleRow key={`${item.time}-${item.title}`} item={item} isLast={index === items.length - 1} />
          ))}
        </ol>
      )}
    </section>
  );
}
